import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Download } from "lucide-react";
import { useBillingHistory } from "@/api/hooks/use-query-api";
import { miniAppBridgeService } from "@/api/services";

// Helper functions for formatting API data
const formatInvoiceDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatAmount = (amount: number, currency: string): string => {
  return `${currency.toUpperCase()} $${(amount / 100).toFixed(2)}`;
};

const formatInvoiceId = (id: string): string => {
  return `Invoice ${id.slice(-8)}`;
};

const handleDownloadInvoice = async (
  downloadUrl: string,
  invoiceId: string
) => {
  try {
    // Fetch the file as blob
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `invoice-${invoiceId.slice(-8)}.pdf`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Failed to download invoice:", error);
    // Fallback: open in new tab
    window.open(downloadUrl, "_blank");
  }
};

export const BillingHistorySection: React.FC = () => {
  const { t } = useTranslation();

  // API hook
  const {
    data: billingData,
    isLoading: isBillingLoading,
    error: billingError,
  } = useBillingHistory();

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-primary mb-2">
          {t("settings.billing.billingHistory")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-text-tertiary">
          {t("settings.billing.billingHistoryDescription")}
        </p>
      </div>

      {/* Billing History Table */}
      <div className="bg-white dark:bg-gray-800 border border-border-secondary dark:border-border-secondary rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden lg:block max-h-[400px] overflow-y-auto">
          <div className="grid grid-cols-5">
            {/* Header */}
            <div className="px-5 py-3 bg-white dark:bg-gray-800 border-b border-border-secondary dark:border-border-secondary flex items-center">
              <p className="text-xs font-semibold text-text-quaternary dark:text-text-quaternary">
                {t("settings.billing.invoice")}
              </p>
            </div>
            <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-border-secondary dark:border-border-secondary flex items-center">
              <div className="flex items-center gap-1">
                <p className="text-xs font-semibold text-text-quaternary dark:text-text-quaternary">
                  {t("settings.billing.billingDate")}
                </p>
                <ChevronDown className="w-3 h-3 text-text-quaternary dark:text-text-quaternary" />
              </div>
            </div>
            <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-border-secondary dark:border-border-secondary flex items-center">
              <p className="text-xs font-semibold text-text-quaternary dark:text-text-quaternary">
                {t("settings.billing.status")}
              </p>
            </div>
            <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-border-secondary dark:border-border-secondary flex items-center">
              <p className="text-xs font-semibold text-text-quaternary dark:text-text-quaternary">
                {t("settings.billing.amount")}
              </p>
            </div>
            <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-border-secondary dark:border-border-secondary flex items-center">
              <p className="text-xs font-semibold text-text-quaternary dark:text-text-quaternary">
                {t("settings.billing.actions")}
              </p>
            </div>

            {/* Rows */}
            {isBillingLoading ? (
              <div className="col-span-5 px-5 py-8 text-center">
                <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                  {t("common.loading")}...
                </p>
              </div>
            ) : billingError ? (
              <div className="col-span-5 px-5 py-8 text-center">
                <p className="text-sm text-red-500 dark:text-red-400">
                  {t("error.unknown")}
                </p>
              </div>
            ) : billingData?.data.invoices.length === 0 ? (
              <div className="col-span-5 px-5 py-8 text-center">
                <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                  {t("settings.billing.noInvoices")}
                </p>
              </div>
            ) : (
              billingData?.data.invoices.map((invoice, index) => (
                <React.Fragment key={invoice.id}>
                  <div
                    className={`px-5 py-4 flex items-center ${
                      index !== billingData.data.invoices.length - 1
                        ? "border-b border-border-secondary dark:border-border-secondary"
                        : ""
                    }`}
                  >
                    <p className="text-sm font-medium text-text-primary dark:text-text-primary">
                      {formatInvoiceId(invoice.id)}
                    </p>
                  </div>
                  <div
                    className={`px-6 py-4 flex items-center ${
                      index !== billingData.data.invoices.length - 1
                        ? "border-b border-border-secondary dark:border-border-secondary"
                        : ""
                    }`}
                  >
                    <p className="text-sm text-text-primary dark:text-text-primary">
                      {formatInvoiceDate(invoice.created)}
                    </p>
                  </div>
                  <div
                    className={`px-6 py-4 flex items-center ${
                      index !== billingData.data.invoices.length - 1
                        ? "border-b border-border-secondary dark:border-border-secondary"
                        : ""
                    }`}
                  >
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        invoice.status === "paid"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                      }`}
                    >
                      {invoice.status === "paid"
                        ? t("settings.billing.paid")
                        : invoice.status}
                    </span>
                  </div>
                  <div
                    className={`px-6 py-4 flex items-center ${
                      index !== billingData.data.invoices.length - 1
                        ? "border-b border-border-secondary dark:border-border-secondary"
                        : ""
                    }`}
                  >
                    <p className="text-sm text-text-primary dark:text-text-primary">
                      {formatAmount(invoice.amount, invoice.currency)}
                    </p>
                  </div>
                  <div
                    className={`px-6 py-4 flex items-center ${
                      index !== billingData.data.invoices.length - 1
                        ? "border-b border-border-secondary dark:border-border-secondary"
                        : ""
                    }`}
                  >
                    {!miniAppBridgeService.isInMiniApp() && (
                      <button
                        onClick={() =>
                          handleDownloadInvoice(invoice.downloadUrl, invoice.id)
                        }
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        {t("settings.billing.download")}
                      </button>
                    )}
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        {/* Mobile Table */}
        <div className="lg:hidden max-h-[300px] overflow-y-auto">
          {isBillingLoading ? (
            <div className="p-4 text-center">
              <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                {t("common.loading")}...
              </p>
            </div>
          ) : billingError ? (
            <div className="p-4 text-center">
              <p className="text-sm text-red-500 dark:text-red-400">
                {t("error.unknown")}
              </p>
            </div>
          ) : billingData?.data.invoices.length === 0 ? (
            <div className="p-4 text-center">
              <p className="text-sm text-text-tertiary dark:text-text-tertiary">
                {t("settings.billing.noInvoices")}
              </p>
            </div>
          ) : (
            billingData?.data.invoices.map((invoice, index) => (
              <div
                key={invoice.id}
                className={`p-4 ${
                  index !== billingData.data.invoices.length - 1
                    ? "border-b border-border-secondary dark:border-border-secondary"
                    : ""
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <p className="text-sm font-medium text-text-primary dark:text-text-primary">
                    {formatInvoiceId(invoice.id)}
                  </p>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      invoice.status === "paid"
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                    }`}
                  >
                    {invoice.status === "paid"
                      ? t("settings.billing.paid")
                      : invoice.status}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm text-text-tertiary dark:text-text-tertiary mb-2">
                  <span>{formatInvoiceDate(invoice.created)}</span>
                  <span>{formatAmount(invoice.amount, invoice.currency)}</span>
                </div>
                {!miniAppBridgeService.isInMiniApp() && (
                  <div className="flex justify-end">
                    <button
                      onClick={() =>
                        handleDownloadInvoice(invoice.downloadUrl, invoice.id)
                      }
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 rounded-md hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      {t("settings.billing.download")}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
